import { ChevronDown, Truck01 } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment-timezone';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DELIVERY_DATE_TYPE_OPTIONS, DELIVERY_WINDOW_TYPE_OPTIONS } from 'src/app/MyQuote/AddQuote/Steps/addQuoteSteps.const';
import { getPhysicallyActiveLeg } from 'src/app/Orders/OrderStatus/nba.helpers';
import { getTransitTimeRangeDisplay } from 'src/app/Orders/schedule.helpers';
import { getLatestStartedLegLocation } from 'src/app/Track/track.helpers';
import ButtonCmp from 'src/components/ButtonCmp';
import CommonModal from 'src/components/CommonModal';
import HeaderCmp from 'src/components/HeaderCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { ORDER_STATUS } from 'src/constants/common';
import { ROUTES } from 'src/constants/routes';
import { appearance } from 'src/redux/basic.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { requestUpdateLocation } from 'src/services/OrderService';
import { getDateWithSuffixFormat, getFormattedNumber } from 'src/utils/CommonFunctions';
import { getPortalOrigin } from 'src/utils/portalOrigin';
import WalToast from 'src/utils/WalToast';

import checkCircle from '../../../../assets/icons/filled-icons/check-circle.svg';
import UploadCustomsDocModal from '../../../Orders/OrderStatus/UploadCustomsDocModal';
import { useRadarData } from '../../hooks/useRadarData';
import { setTrackOrderDetail } from '../../track.slice';
import { formatTimeRangeDisplay, makeFormatScheduleDisplay } from '../track.helpers';

import CardInfoRow from './common/CardInfoRow';
import { SHARE_MOBILE_CARD, SHARE_MOBILE_CARD_TITLE } from './common/shareMobile.styles';

export const checkLocationUpdate = (dateString: string | null, timezone: string | undefined, t: any): string => {
    if (!dateString) return '-';
    const dateUtc = moment.utc(dateString);
    const nowUtc = moment.utc();
    const diffMins = nowUtc.diff(dateUtc, 'minutes');
    if (diffMins < 1) return t('Common.Just_now');
    if (diffMins < 60) return diffMins === 1 ? t('Common.Minute_ago', { count: diffMins }) : t('Common.Minutes_ago', { count: diffMins });
    const diffHours = nowUtc.diff(dateUtc, 'hours');
    if (diffHours < 24) return diffHours === 1 ? t('Common.Hour_ago', { count: diffHours }) : t('Common.Hours_ago', { count: diffHours });
    const yesterdayUtc = moment.utc().subtract(1, 'days').startOf('day');
    const displayMoment = timezone ? dateUtc.clone().tz(timezone) : dateUtc;
    if (dateUtc.isSame(yesterdayUtc, 'day')) return t('Common.Yesterday_at', { time: displayMoment.format('h:mmA') });

    // Only the absolute-date branch carries an ordinal, so it is the only one wrapped as a
    // <sup>. The result is an HTML string — render it via dangerouslySetInnerHTML (the input is
    // a moment-formatted date, never user content) and strip tags for any tooltip/plain use.
    return getDateWithSuffixFormat(displayMoment.format(`MMMM Do YYYY [${t('Common.at')}] h:mmA`));
};

interface IInTransitStateSectionProps {
    isShareLink?: boolean;
    titleContent?: React.ReactNode;
    // Share-link mobile sheet (Schedule tab) — Figma card layout, same data + interactions.
    isShareMobile?: boolean;
}

const InTransitStateSection = ({ isShareLink = false, titleContent, isShareMobile = false }: IInTransitStateSectionProps) => {
    const { t } = useTranslation();
    const { trackOrderDetail: orderDetail, isDetailLoading: isLoading, fullRouteDistance, remainingDistance, selectedLeg } = useRadarData();
    // "Miles to Destination" = what's LEFT, not the full route. remainingDistance is 0
    // until the map's traveled/remaining split computes, so fall back to the full route
    // (which equals the remainder anyway when nothing has moved yet).
    const milesToDestination = remainingDistance || fullRouteDistance;
    const dispatch = useAppDispatch();
    const appearanceData = useAppSelector(appearance);
    const userTimezone = appearanceData?.timezone || 'UTC';

    const [isDetailOpen, setIsDetailOpen] = useState(true);
    const [isUploadCustomsDocOpen, setIsUploadCustomsDocOpen] = useState(false);
    // Set on a successful upload so the button disappears immediately. The API flag below is the
    // durable half — this only covers the window before the detail payload is refetched.
    const [isCustomsDocUploaded, setIsCustomsDocUploaded] = useState(false);
    const [isRequestingUpdateLocation, setIsRequestingUpdateLocation] = useState(false);
    const [isShowLoginRequiredModal, setIsShowLoginRequiredModal] = useState(false);

    const handleRequestUpdateLocation = () => {
        setIsRequestingUpdateLocation(true);
        requestUpdateLocation(orderDetail?.masterOrderId)
            .then((response: any) => {
                const newRequest = response?.data?.data;

                if (newRequest && orderDetail) {
                    const updatedRequests = [...(orderDetail.customerRequests || []), newRequest];
                    dispatch(setTrackOrderDetail({ ...orderDetail, customerRequests: updatedRequests }));
                }
                WalToast.success('Location update requested');
            })
            .catch(() => {
                WalToast.error('Failed to request location update');
            })
            .finally(() => setIsRequestingUpdateLocation(false));
    };

    const fullAddressRef = useRef<HTMLParagraphElement>(null);
    const fullTimeRef = useRef<HTMLParagraphElement>(null);

    // The card's detail rows (pickup/delivery schedule, transit time, customs upload)
    // follow the LEG PAGER — selectedLeg is seeded to the physically active leg on load,
    // so by default this still describes the running leg (matching the order page's NBA
    // card). The "last seen"/progress header stays order-live (started-legs newest ping).
    const activeLeg = useMemo(() => selectedLeg ?? getPhysicallyActiveLeg(orderDetail?.legs) ?? orderDetail?.legs?.[0] ?? null, [selectedLeg, orderDetail]);

    const isUpdateLocationPending = useMemo(
        () => orderDetail?.customerRequests?.some((req: any) => req.type === 'update_location' && req.status === 'pending') ?? false,
        [orderDetail?.customerRequests]
    );
    // Actions belong to the stage the order is actually in. The stage switcher lets a
    // customer VIEW this card on a dispatched or delivered order — those renders stay
    // read-only, so neither the location request nor the customs upload may fire there.
    // Mirrors wal-frontend-v2 RadarOrderDetail's showActions prop.
    const showActions = orderDetail?.orderStatus === ORDER_STATUS.IN_TRANSIT;
    // "Last seen" + progress must come from the newest ping across STARTED legs — the same
    // source as the map's truck marker. Reading the active leg's raw lastLocationUpdate
    // surfaced stale pre-entered pings (e.g. a 99% ping on a leg still dispatched). When no
    // started leg has a ping, this is null and the card falls back to the shipper address.
    const lastLocationUpdate = useMemo(() => getLatestStartedLegLocation(orderDetail?.legs ?? []), [orderDetail]);
    const schedule = activeLeg?.schedule ?? null;

    const lastLocation = lastLocationUpdate?.fullAddress || activeLeg?.shipper?.fullAddress || '';
    const formatCityCountry = (loc: any): string => [loc?.city, loc?.countryCode || loc?.country].filter(Boolean).join(', ');
    const shortLocation = formatCityCountry(lastLocationUpdate) || formatCityCountry(activeLeg?.shipper) || lastLocation;
    const progressPercentage = lastLocationUpdate?.progressPercentage ?? 0;

    // Customs papers are filed on the physical leg that crosses the border. When "Display Order Legs"
    // is off the backend merges internal legs and names that hidden leg as `customsLegId` (the span's
    // own legDbId would be the wrong leg); a real physical leg is its own crossing leg when its
    // shipper and consignee are in different countries. null = no crossing, button hidden.
    const customsLegId = useMemo(() => {
        if (activeLeg?.customsLegId !== undefined) return activeLeg.customsLegId ?? null;

        const shipperCountry = String(activeLeg?.shipper?.country ?? '').toLowerCase();
        const consigneeCountry = String(activeLeg?.consignee?.country ?? '').toLowerCase();

        return shipperCountry && consigneeCountry && shipperCountry !== consigneeCountry ? activeLeg?.legDbId ?? null : null;
    }, [activeLeg]);
    const isCrossBorder = customsLegId != null;
    // Customs paperwork is filed once — once this leg has a customs document there is nothing
    // left to upload, so the button goes away rather than inviting a duplicate.
    const hasCustomsDocument = Boolean(activeLeg?.hasCustomsDocument) || isCustomsDocUploaded;

    // 'MMM Do' (no year) — same format WAL's in-transit card passes, so "Est. Delivery Date" reads
    // "Friday, Jul 10th" on both sides instead of the helper's default "MMM Do, YYYY" here.
    const isPickupDateObj = useMemo(() => makeFormatScheduleDisplay('pickup', schedule, activeLeg?.shipper?.timezone, 'MMM Do'), [schedule, activeLeg]);
    const isDeliveryDateObj = useMemo(() => makeFormatScheduleDisplay('delivery', schedule, activeLeg?.consignee?.timezone, 'MMM Do'), [schedule, activeLeg]);

    // formattedDateRange already carries <sup> ordinals from the shared helper — CardInfoRow's
    // string path renders it as HTML, so no further suffix formatting is applied here.
    const targetPickupDate = isPickupDateObj?.display?.formattedDateRange ? `${isPickupDateObj.display.formattedDateRange} ${formatTimeRangeDisplay(isPickupDateObj.display, t)}`.trim() : '-';

    const targetDeliveryDate = isDeliveryDateObj?.display?.formattedDateRange ? `${isDeliveryDateObj.display.formattedDateRange} ${formatTimeRangeDisplay(isDeliveryDateObj.display, t)}`.trim() : '-';

    const actualPickupDatetime = schedule?.actualPickupDatetime ?? null;
    const isPickedUpOrder = Boolean(orderDetail?.orderStatus && orderDetail.orderStatus !== ORDER_STATUS.DISPATCHED && actualPickupDatetime);
    // Actual pickup is shown in the VIEWER's timezone — same as WAL's in-transit card and the
    // "Last Update" line on this very card. It used to use the shipper's zone, which put Pickup
    // Time an hour off from Last Update whenever the shipper sits in a different zone.
    const actualPickupMoment = actualPickupDatetime ? moment.utc(actualPickupDatetime).tz(userTimezone) : null;
    // Moment escapes literal text wrapped in [..]; injecting the translated word into the
    // brackets keeps it a single format string while letting i18n control the separator.
    const atToken = `[${t('Common.at')}]`;
    const pickedUpLabel = actualPickupMoment
        ? getDateWithSuffixFormat(
              actualPickupMoment.calendar(null, {
                  sameDay: `[Today], ${atToken} HH:mm`,
                  lastDay: `[Yesterday], ${atToken} HH:mm`,
                  lastWeek: `MMM Do, YYYY ${atToken} HH:mm`,
                  nextDay: `MMM Do, YYYY ${atToken} HH:mm`,
                  nextWeek: `MMM Do, YYYY ${atToken} HH:mm`,
                  sameElse: `MMM Do, YYYY ${atToken} HH:mm`,
              })
          )
        : '';

    const pickupTimeValue = isPickedUpOrder ? pickedUpLabel : targetPickupDate;

    const sensitivityLabel = DELIVERY_DATE_TYPE_OPTIONS.find((opt) => opt.value === schedule?.currentDeliverySensitivity)?.label;
    const windowTypeLabel = DELIVERY_WINDOW_TYPE_OPTIONS.find((opt) => opt.value === schedule?.currentDeliveryWindowType)?.label;
    const deliveryTimeRange = isDeliveryDateObj?.display?.timeRange && isDeliveryDateObj.display.timeRange !== '-' ? isDeliveryDateObj.display.timeRange : '';
    const deliveryWindowValue = windowTypeLabel || deliveryTimeRange ? `${windowTypeLabel ? `${windowTypeLabel} ` : ''}${deliveryTimeRange}`.trim() : '-';

    const transitTimeDisplay = useMemo(() => getTransitTimeRangeDisplay(schedule), [schedule]);

    const lastUpdateDisplay = checkLocationUpdate(lastLocationUpdate?.updatedAt ?? schedule?.actualPickupDatetime ?? null, userTimezone, t);
    const lastUpdatePlain = lastUpdateDisplay.replace(/<[^>]*>/g, '');

    const requestUpdateAction = !showActions ? null : isUpdateLocationPending ? (
        <span className="inline-flex">
            {/* Request is already in flight — show a green check and keep the label in its normal
                        (non-dimmed) color, but block every interaction until WAL acts on the request.
                        ButtonCmp's `disabled` prop is deliberately NOT used here: it dims the text via
                        opacity-60, which is exactly the greyed-out look we're moving away from. The
                        pointer-events-none class reproduces only the "not clickable" half of that styling,
                        and there is no onClick so keyboard activation is a no-op too. */}
            <ButtonCmp
                type="button"
                className="text-xs text-primary !py-0 font-semibold !shadow-none pointer-events-none cursor-default"
                icon={<img src={checkCircle} alt="check" className="w-4 h-4" />}
                iconSide="left"
            >
                {t('Orders.Location_Update_Requested')}
            </ButtonCmp>
        </span>
    ) : (
        <ButtonCmp
            type="button"
            className="text-xs text-primary font-semibold !shadow-none !py-0"
            onClick={isShareLink ? () => setIsShowLoginRequiredModal(true) : handleRequestUpdateLocation}
            disabled={!isShareLink && (isLoading || isRequestingUpdateLocation)}
            loading={isRequestingUpdateLocation}
        >
            {t('Orders.Request_Update_Location')}
        </ButtonCmp>
    );

    const modals = (
        <>
            {isUploadCustomsDocOpen && (
                <UploadCustomsDocModal
                    orderLegId={customsLegId}
                    handleClose={() => setIsUploadCustomsDocOpen(false)}
                    handleOnSubmit={() => {
                        setIsCustomsDocUploaded(true);
                        setIsUploadCustomsDocOpen(false);
                    }}
                />
            )}

            {isShowLoginRequiredModal && (
                <CommonModal
                    title={<h6 className="text-textSecondary text-base lg:text-lg font-semibold">{t('Orders.Login_Required_Title')}</h6>}
                    modalTitleDesc={<p className="text-grayLight600 text-xs leading-[18px] lg:text-sm font-normal">{t('Orders.Login_Required_Desc')}</p>}
                    modalHeaderClass="border-b-0 !px-6 !pt-6 !pb-5 !bg-transparent"
                    modalHeaderTextClass="!flex-col"
                    modalFooterClass="border-t-0 !bg-transparent"
                    size="max-w-[445px]"
                    primaryBtnText={t('QuotingHub.Common.Continue') as string}
                    secondaryBtnText={t('Common.Cancel') as string}
                    primaryBtnOnClick={() => {
                        // The portal's origin, not this one: on the public tracking hostname /login does not exist.
                        window.location.href = `${getPortalOrigin()}${ROUTES.LOGIN}`;
                    }}
                    secondaryBtnOnClick={() => setIsShowLoginRequiredModal(false)}
                    handleClose={() => setIsShowLoginRequiredModal(false)}
                >
                    <></>
                </CommonModal>
            )}
        </>
    );

    if (isShareMobile) {
        return (
            <>
                <div className={`${SHARE_MOBILE_CARD} p-2 flex flex-col gap-2`}>
                    {/* Header — switcher + collapse toggle, request-update action on the right */}
                    <div className="flex items-center justify-between gap-2 pb-1">
                        <div className="flex items-center gap-2 min-w-0">{titleContent ?? <p className={SHARE_MOBILE_CARD_TITLE}>{t('Radar.In_Transit_Schedule')}</p>}</div>
                        {requestUpdateAction}
                    </div>

                    {/* Last seen + progress */}
                    <div className="flex items-start gap-1.5">
                        <div className="h-[38px] w-[38px] rounded border border-utilityGray200 bg-white flex items-center justify-center flex-none">
                            <Truck01 className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                            <TruncateTextCmp refName={fullAddressRef} tooltipType="action" message={lastLocation}>
                                <p className={`text-xs font-medium leading-[18px] text-grayLight900 truncate ${isLoading ? 'custom-loading' : ''}`} ref={fullAddressRef}>
                                    {isLoading
                                        ? 'lorem ipsum dolor'
                                        : !isPickedUpOrder
                                        ? t('Radar.No_Transit_Updates_Yet')
                                        : shortLocation
                                        ? t('Radar.Last_Seen_Near', { location: shortLocation })
                                        : '-'}
                                </p>
                            </TruncateTextCmp>
                            <TruncateTextCmp refName={fullTimeRef} tooltipType="action" message={lastUpdatePlain}>
                                <p
                                    className={`text-xs font-medium leading-[18px] text-grayLight600 truncate ${isLoading ? 'custom-loading' : ''}`}
                                    ref={fullTimeRef}
                                    dangerouslySetInnerHTML={{
                                        __html: isLoading ? 'lorem ipsum dolor' : !isPickedUpOrder ? t('Radar.The_pickup_is_still_pending') : t('Radar.Last_Update', { time: lastUpdateDisplay }),
                                    }}
                                />
                            </TruncateTextCmp>
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <div className="flex justify-between gap-x-2">
                            <p className={`text-grayLight600 text-xs leading-[18px] font-medium ${isLoading ? 'custom-loading' : ''}`}>{`${progressPercentage}% ${t('Common.Complete')}`}</p>
                            <p className={`text-gray500 text-xs leading-[18px] font-normal ${isLoading ? 'custom-loading' : ''}`}>
                                {milesToDestination ? `${getFormattedNumber(Math.round(milesToDestination), false, false, true)} ${t('Radar.Miles_to_Destination')}` : '-'}
                            </p>
                        </div>
                        <div className="progress-range-container">
                            <div className={`progress-range-bar progress-success ${isLoading ? 'custom-loading' : ''}`} style={{ width: `${progressPercentage}%` }} />
                        </div>
                    </div>

                    {/* Detail rows — always shown in full on the share sheet */}
                    <div className="border-t border-utilityGray200 pt-2 flex flex-col gap-2">
                        <CardInfoRow isShareMobile label={t('Radar.Pickup_Time')} value={pickupTimeValue} isLoading={isLoading} isLabelLoading={isLoading} />
                        <CardInfoRow isShareMobile label={t('Common.Est_Transit_Time')} value={transitTimeDisplay} isLoading={isLoading} isLabelLoading={isLoading} />
                        <CardInfoRow isShareMobile label={t('Radar.Delivery_Sensitivity')} value={sensitivityLabel} isLoading={isLoading} isLabelLoading={isLoading} />
                        <CardInfoRow isShareMobile label={t('Radar.Est_Delivery_Date')} value={targetDeliveryDate} isLoading={isLoading} isLabelLoading={isLoading} align="start" />
                        <CardInfoRow isShareMobile label={t('Radar.Delivery_Window')} value={deliveryWindowValue} isLoading={isLoading} isLabelLoading={isLoading} />
                    </div>

                    {showActions && isCrossBorder && !hasCustomsDocument && (
                        <div className="flex items-center justify-end gap-x-2">
                            <ButtonCmp
                                className={`btn-outline-primary ${isLoading ? 'custom-loading' : ''}`}
                                onClick={isShareLink ? () => setIsShowLoginRequiredModal(true) : () => setIsUploadCustomsDocOpen(true)}
                                disabled={isLoading}
                            >
                                {t('Orders.Upload_Customs_Documents')}
                            </ButtonCmp>
                        </div>
                    )}
                </div>

                {modals}
            </>
        );
    }

    return (
        <>
            <div className="w-full rounded-lg shadow-md bg-white overflow-hidden">
                {/* Header */}
                <HeaderCmp
                    title={titleContent ? '' : t('Radar.In_Transit_Schedule')}
                    leftSideContent={titleContent}
                    isHeaderWithBg={false}
                    parentClassName="!border-b-0 px-4"
                    rightSideContent={requestUpdateAction}
                />

                {/* Body */}
                <div className="px-4 pb-4 flex flex-col gap-y-3">
                    {/* Location + progress card */}
                    <div className="p-3 rounded-lg border flex flex-col border-utilityGray200 bg-gray50 overflow-hidden">
                        <div className="flex items-start gap-x-1 cursor-pointer" onClick={() => setIsDetailOpen((prev) => !prev)}>
                            <div className="table-profile-customer-circle-xl rounded-md bg-white flex flex-col items-center justify-center text-center flex-none gap-[1px] w-9 h-9">
                                <Truck01 className="w-5 h-5 text-primary" />
                            </div>

                            <div className="flex-1 min-w-0">
                                <TruncateTextCmp refName={fullAddressRef} tooltipType="action" message={lastLocation} onClick={() => setIsDetailOpen((prev) => !prev)}>
                                    <p className={`text-[12px] font-medium leading-[18px] text-grayLight900 truncate ${isLoading ? 'custom-loading' : ''}`} ref={fullAddressRef}>
                                        {isLoading
                                            ? 'lorem ipsum dolor'
                                            : !isPickedUpOrder
                                            ? t('Radar.No_Transit_Updates_Yet')
                                            : shortLocation
                                            ? t('Radar.Last_Seen_Near', { location: shortLocation })
                                            : '-'}
                                    </p>
                                </TruncateTextCmp>
                                <TruncateTextCmp refName={fullTimeRef} tooltipType="action" message={lastUpdatePlain} onClick={() => setIsDetailOpen((prev) => !prev)}>
                                    <p
                                        className={`text-[12px] font-normal leading-[18px] text-grayLight600 truncate ${isLoading ? 'custom-loading' : ''}`}
                                        ref={fullTimeRef}
                                        dangerouslySetInnerHTML={{
                                            __html: isLoading ? 'lorem ipsum dolor' : !isPickedUpOrder ? t('Radar.The_pickup_is_still_pending') : t('Radar.Last_Update', { time: lastUpdateDisplay }),
                                        }}
                                    />
                                </TruncateTextCmp>
                            </div>

                            <ChevronDown className={`w-4 h-4 text-primary my-auto flex-none transition-transform duration-200 ${isDetailOpen ? 'rotate-180' : ''}`} />
                        </div>

                        {/* Progress bar */}
                        <div className="space-y-1.5 mt-2">
                            <div className="flex justify-between gap-x-2">
                                <p className={`text-textSecondary text-xs leading-[18px] font-medium ${isLoading ? 'custom-loading' : ''}`}>{`${progressPercentage}% ${t('Common.Complete')}`}</p>
                                <p className={`text-gray500 text-xs leading-[18px] font-normal ${isLoading ? 'custom-loading' : ''}`}>
                                    {/* Math.round before formatting: WAL's getFormattedNumber rounds when isDecimal=false, the
                                        portal's truncates — without this 209.6 mi reads 209 here and 210 on WAL. */}
                                    {milesToDestination ? `${getFormattedNumber(Math.round(milesToDestination), false, false, true)} ${t('Radar.Miles_to_Destination')}` : '-'}
                                </p>
                            </div>
                            <div className="progress-range-container">
                                <div className={`progress-range-bar progress-success ${isLoading ? 'custom-loading' : ''}`} style={{ width: `${progressPercentage}%` }} />
                            </div>
                        </div>

                        {/* Collapsible detail rows */}
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateRows: isDetailOpen ? '1fr' : '0fr',
                                transition: 'grid-template-rows 300ms ease',
                            }}
                        >
                            <div className="overflow-hidden">
                                <div className="border mt-3 bg-white border-utilityGray200 px-3 py-3 rounded-md flex flex-col gap-y-2">
                                    {/* Values carry <sup> ordinals — CardInfoRow's string path renders them as
                                        HTML so the tags don't show as literal text. */}
                                    <CardInfoRow label={t('Radar.Pickup_Time')} value={pickupTimeValue} isLoading={isLoading} isLabelLoading={isLoading} />
                                    <CardInfoRow label={t('Common.Est_Transit_Time')} value={transitTimeDisplay} isLoading={isLoading} isLabelLoading={isLoading} />
                                    <CardInfoRow label={t('Radar.Delivery_Sensitivity')} value={sensitivityLabel} isLoading={isLoading} isLabelLoading={isLoading} />
                                    <CardInfoRow label={t('Radar.Est_Delivery_Date')} value={targetDeliveryDate} isLoading={isLoading} isLabelLoading={isLoading} align="start" />
                                    <CardInfoRow label={t('Radar.Delivery_Window')} value={deliveryWindowValue} isLoading={isLoading} isLabelLoading={isLoading} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action buttons */}
                    {showActions && isCrossBorder && !hasCustomsDocument && (
                        <div className="flex items-center justify-end gap-x-2">
                            <ButtonCmp
                                className={`btn-outline-primary ${isLoading ? 'custom-loading' : ''}`}
                                onClick={isShareLink ? () => setIsShowLoginRequiredModal(true) : () => setIsUploadCustomsDocOpen(true)}
                                disabled={isLoading}
                            >
                                {t('Orders.Upload_Customs_Documents')}
                            </ButtonCmp>
                        </div>
                    )}
                </div>
            </div>

            {modals}
        </>
    );
};

export default InTransitStateSection;
